import { useEffect, useRef, useState } from 'react';
import Globe from 'react-globe.gl';

function GlobeComponent() {
  const globeEl = useRef();
  const [selectedCity, setSelectedCity] = useState(null);
  const [countries, setCountries] = useState([]);
  const [globeReady, setGlobeReady] = useState(false);
  const [texReady, setTexReady] = useState(false);

  const offices = [
    {
      name: 'Dubai', lat: 25.2048, lng: 55.2708, color: '#5BC0BE',
      company: 'Sinocom Global Trading LLC', country: 'United Arab Emirates', countryCode: 'ae',
      info: 'Middle East & Africa Hub', size: 1,
      address: 'Shams Business Center, Sharjah Media City Free Zone, Al Messaned, Sharjah, UAE',
      contact: 'Sanjay Chawla - +971 50 186 6343', whatsapp: '971501866343', email: 'sinocom.ae@outlook.com',
      details: 'End-to-end logistics: complete supply-chain execution from dispatch to final delivery, regional distribution, and importer support.',
      timezone: 'UTC+4', language: 'Arabic, English'
    },
    {
      name: 'Bangkok', lat: 13.7563, lng: 100.5018, color: '#E0936B',
      company: 'Salina Intertrade Co., Ltd.', country: 'Thailand', countryCode: 'th',
      info: 'Southeast Asia Gateway', size: 1,
      address: 'House No. 28, Sukhumvit Soi 29 Alley, Khlong Tan Neau, Vadhana, 10110, Bangkok, Thailand',
      contact: 'Pankaj Ahuja - +66 955581160', whatsapp: '66955581160',
      details: 'Regional logistics hub facilitating trade between China and Southeast Asian markets.',
      timezone: 'UTC+7', language: 'Thai, English'
    },
    {
      name: 'Hong Kong', lat: 22.3193, lng: 114.1694, color: '#D7A94A',
      company: 'Sinocom Global Limited', country: 'Hong Kong SAR', countryCode: 'hk',
      info: 'Global Headquarters', size: 1.2,
      address: 'RM A 9/F Silvercorp Intl Tower, 707-713 Nathan Rd, Mongkok, Kowloon',
      contact: 'Pankaj Ahuja - +86 156 2722 3593', whatsapp: '8615627223593',
      details: 'Global headquarters overseeing international trade operations, financial services, and business development.',
      timezone: 'UTC+8', language: 'Cantonese, English, Mandarin'
    },
    {
      name: 'Mumbai', lat: 19.0760, lng: 72.8777, color: '#D98AAE',
      company: 'Jai Shivam Creation', country: 'India', countryCode: 'in',
      info: 'South Asia Operations', size: 1,
      address: '87-89 Trinity House, Ground Floor, S S Gaikwad Marg, Dhobi Talao, Mumbai 400 002',
      contact: 'Kishore Bhagwandas Dodeja - +91 78208 54392', whatsapp: '917820854392',
      details: 'Export operations to Dubai and the Middle East — goods consolidation and logistics from Indian manufacturers.',
      timezone: 'UTC+5:30', language: 'Hindi, English, Marathi'
    },
    {
      name: 'Yiwu', lat: 29.3167, lng: 120.0750, color: '#8E86C9',
      company: 'Yiwu Fusheng Trading Firm', country: 'China', countryCode: 'cn',
      info: 'Commodity Trading Center', size: 1,
      address: 'Room 601, 6th Floor, Building 6, Duhui Wenlan, Yiwu City, Zhejiang Province',
      addressCn: '浙江省义乌市都会文澜6栋6楼601室',
      contact: 'Gulshan Gordhadas Khatri - +86 15057910421', whatsapp: '8615057910421',
      details: 'Full-service trade hub: consolidation from Chinese provinces, on-the-ground inspection, export documentation, financial services, and buyer assistance.',
      timezone: 'UTC+8', language: 'Mandarin Chinese'
    }
  ];

  // Every real shipping destination from the trade archive. The Dubai, Mumbai
  // and Bangkok offices already cover UAE, India and Thailand, so those aren't
  // repeated here.
  const destinations = [
    { name: 'Maputo', lat: -25.9692, lng: 32.5732, country: 'Mozambique' },
    { name: 'Banjul', lat: 13.4549, lng: -16.5790, country: 'Gambia' },
    { name: 'Lagos', lat: 6.5244, lng: 3.3792, country: 'Nigeria' },
    { name: 'Freetown', lat: 8.4606, lng: -13.2317, country: 'Sierra Leone' },
    { name: 'Lusaka', lat: -15.3875, lng: 28.3228, country: 'Zambia' },
    { name: 'Lilongwe', lat: -13.9626, lng: 33.7741, country: 'Malawi' },
    { name: 'Kinshasa', lat: -4.4419, lng: 15.2663, country: 'DR Congo' },
    { name: 'Zanzibar', lat: -6.1659, lng: 39.2026, country: 'Tanzania' },
    { name: 'Mamoudzou', lat: -12.7806, lng: 45.2278, country: 'Mayotte' },
    { name: 'Saint-Denis', lat: -20.8789, lng: 55.4481, country: 'Réunion' },
    { name: 'Jeddah', lat: 21.4858, lng: 39.1925, country: 'Saudi Arabia' },
    { name: 'Tehran', lat: 35.6892, lng: 51.3890, country: 'Iran' },
    { name: 'Madrid', lat: 40.4168, lng: -3.7038, country: 'Spain' },
    { name: 'Monrovia', lat: 6.3008, lng: -10.7970, country: 'Liberia' },
    { name: 'Port of Spain', lat: 10.6596, lng: -61.5089, country: 'Trinidad & Tobago' },
    { name: 'Castries', lat: 14.0101, lng: -60.9875, country: 'St Lucia' },
    { name: 'Nassau', lat: 25.0480, lng: -77.3554, country: 'Bahamas' },
    { name: 'Istanbul', lat: 41.0082, lng: 28.9784, country: 'Turkey' },
    { name: 'Colombo', lat: 6.9271, lng: 79.8612, country: 'Sri Lanka' },
    { name: 'Manila', lat: 14.5995, lng: 120.9842, country: 'Philippines' },
    { name: 'Yangon', lat: 16.8409, lng: 96.1735, country: 'Myanmar' }
  ];

  const DEST_COLOR = '#FF5C5C';

  useEffect(() => {
    const globe = globeEl.current;
    if (!globe) return;
    const controls = globe.controls();
    // Wheel scrolls the page — never zooms/interacts with the globe.
    controls.enableZoom = false;
    // Lock the vertical tilt so the globe can only spin left↔right (azimuth),
    // never pitch up/down. Polar angle is fixed to the lat:-2 framing.
    const polar = (90 - (-2)) * Math.PI / 180;
    controls.minPolarAngle = polar;
    controls.maxPolarAngle = polar;
    // Start framed on Africa — the overwhelming majority of the business — then
    // let it drift in a very slow ambient rotation.
    globe.pointOfView({ lat: -2, lng: 22, altitude: 2.05 }, 1000);
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.3; // very slow
  }, []);

  // Load country borders for the political overlay, drawn as thin lines on the
  // satellite globe (countries.geojson lives in /public/globe).
  useEffect(() => {
    fetch('/globe/countries.geojson')
      .then((r) => r.json())
      .then((geo) => setCountries(geo.features))
      .catch(() => {});
  }, []);

  // Preload the earth texture so the placeholder only lifts once the real
  // globe will actually have something to show — not a black sphere.
  useEffect(() => {
    const img = new Image();
    img.onload = () => setTexReady(true);
    img.src = '/globe/earth-blue-marble.jpg';
  }, []);

  // close modal on Escape
  useEffect(() => {
    if (!selectedCity) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedCity(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedCity]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div id="globe-section" className="globe-wrap" style={{ touchAction: 'pan-y' }}>
      {/* Hero */}
      <div className="hero">
        <div className="hero-inner">
          <h1 className="hero-title">Four decades moving goods <em>out of China.</em></h1>
          <p className="hero-sub">
            Consolidation, inspection, export documentation and container logistics — from Yiwu and
            Hong Kong to Dubai, and on across Africa and the Middle East.
          </p>
          <div className="hero-actions">
            <button className="btn btn-primary" onClick={() => scrollTo('showcase')}>See the work</button>
            <button className="btn btn-ghost" onClick={() => scrollTo('contact')}>Talk to an office</button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="globe-legend">
        <div className="legend-title">Global Network</div>
        <div className="legend-rows">
          {offices.map((o) => (
            <div className="legend-row" key={o.name}>
              <span className="legend-dot" style={{ background: o.color }} />
              {o.name}{o.info === 'Global Headquarters' ? ' (HQ)' : ''}
            </div>
          ))}
          <div className="legend-row">
            <span className="legend-dot" style={{ background: DEST_COLOR }} />
            Export destinations
          </div>
        </div>
      </div>

      <div className="scroll-hint">Scroll</div>

      {/* Instant placeholder covering the slow-network load gap */}
      <div className="globe-poster" data-ready={globeReady && texReady ? 'true' : 'false'}>
        <div className="globe-poster-ball" />
      </div>

      <Globe
        ref={globeEl}
        onGlobeReady={() => setGlobeReady(true)}
        globeImageUrl="/globe/earth-blue-marble.jpg"
        backgroundImageUrl="/globe/night-sky.png"
        polygonsData={countries}
        polygonCapColor={() => 'rgba(0,0,0,0)'}
        polygonSideColor={() => 'rgba(0,0,0,0)'}
        polygonStrokeColor={() => 'rgba(239,235,223,0.45)'}
        polygonAltitude={0.006}
        pointsData={[
          ...offices,
          ...destinations.map((d) => ({ ...d, color: DEST_COLOR, size: 0.62, isDestination: true, info: 'Trade destination' }))
        ]}
        pointLat={(d) => d.lat}
        pointLng={(d) => d.lng}
        pointColor={(d) => d.color}
        pointAltitude={0.02}
        pointRadius={(d) => d.size || 0.5}
        pointLabel={(d) => `
          <div style="background: rgba(10,14,23,0.92); padding: 9px 12px; border-radius: 8px; color: #EFEBDF; border: 1px solid ${d.color}; font-family: 'Hanken Grotesk', sans-serif;">
            <b style="font-size: ${d.isDestination ? '12px' : '15px'}; color: ${d.color};">${d.name}</b><br/>
            ${d.company ? `<span style="font-size: 13px; color: #EFEBDF;">${d.company}</span><br/>` : ''}
            ${d.country && !d.company ? `<span style="font-size: 12px; color: #AEB6C4;">${d.country}</span><br/>` : ''}
            <span style="font-size: 12px; color: #8B95A6;">${d.info}</span>
            ${!d.isDestination ? `<br/><i style="font-size: 11px; color: ${d.color};">Click for details</i>` : ''}
          </div>
        `}
        onPointClick={(d) => !d.isDestination && setSelectedCity(d)}
      />

      {selectedCity && (
        <div className="modal" onClick={() => setSelectedCity(null)}>
          <div className="modal-card" role="dialog" aria-modal="true" aria-label={`${selectedCity.name} office`} onClick={(e) => e.stopPropagation()}>
            <span className="eyebrow" style={{ color: selectedCity.color }}>{selectedCity.info}</span>
            <h2 className="modal-city">{selectedCity.name}</h2>
            <p className="modal-co">{selectedCity.company}</p>
            <span className="modal-country">{selectedCity.country}</span>

            <div className="modal-grid">
              <div className="modal-block">
                <h4>What we do here</h4>
                <p>{selectedCity.details}</p>
              </div>
              {selectedCity.address && (
                <div className="modal-block">
                  <h4>Address</h4>
                  <p>{selectedCity.address}</p>
                  {selectedCity.addressCn && <p style={{ color: '#8B95A6' }}>{selectedCity.addressCn}</p>}
                </div>
              )}
              <div className="modal-block">
                <h4>Details</h4>
                <p>Timezone: {selectedCity.timezone}</p>
                <p>Languages: {selectedCity.language}</p>
                {selectedCity.contact && <p>Contact: {selectedCity.contact.split(' - ')[0]}</p>}
                {selectedCity.whatsapp && (
                  <p>WhatsApp: <a href={`https://wa.me/${selectedCity.whatsapp}`} target="_blank" rel="noopener noreferrer">{selectedCity.contact.split(' - ')[1]}</a></p>
                )}
                {selectedCity.email && (
                  <p>Email: <a href={`mailto:${selectedCity.email}`}>{selectedCity.email}</a></p>
                )}
              </div>
            </div>

            <button className="modal-close" aria-label="Close" onClick={() => setSelectedCity(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobeComponent;
